import React, { useState, useEffect } from 'react';
import { HashRouter, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import Sidebar from './components/Sidebar';
import AuthModal from './components/AuthModal';
import UpgradeModal from './components/UpgradeModal';
import OnboardingQuiz from './components/OnboardingQuiz';
import NotificationCenter from './components/NotificationCenter';
import LandingPage from './pages/LandingPage';
import ChatPage from './pages/ChatPage';
import RecipesPage from './pages/RecipesPage';
import ProfilePage from './pages/ProfilePage';
import TipsPage from './pages/TipsPage';
import SubscriptionPage from './pages/SubscriptionPage';
import JournalPage from './pages/JournalPage';
import WeightPage from './pages/WeightPage';
import MealPlanPage from './pages/MealPlanPage';
import ScannerPage from './pages/ScannerPage';
import ChallengesPage from './pages/ChallengesPage';
import AdminPage from './pages/AdminPage';
import MentionsLegalesPage from './pages/MentionsLegalesPage';
import PolitiqueConfidentialitePage from './pages/PolitiqueConfidentialitePage';
import CGVPage from './pages/CGVPage';
import useTracking from './hooks/useTracking';
import { Menu, X } from 'lucide-react';
import './App.css';
import './styles/Landing.css';

// Layout principal avec sidebar
const MainLayout = ({ children, onAuthClick }) => {
  const { user, isAuthenticated, authMessage, clearAuthMessage } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authModalMode, setAuthModalMode] = useState('signup');
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const getActivePage = () => {
    const path = location.pathname;
    if (path === '/recettes') return 'recipes';
    if (path === '/profil') return 'profile';
    if (path === '/conseils') return 'tips';
    if (path === '/abonnement') return 'subscription';
    if (path === '/journal') return 'journal';
    if (path === '/poids') return 'weight';
    if (path === '/plans') return 'mealplan';
    if (path === '/scanner') return 'scanner';
    if (path === '/defis') return 'challenges';
    if (path === '/admin') return 'admin';
    return 'chat';
  };

  const setActivePage = (page) => {
    setSidebarOpen(false);
    switch (page) {
      case 'recipes': navigate('/recettes'); break;
      case 'profile': navigate('/profil'); break;
      case 'tips': navigate('/conseils'); break;
      case 'subscription': navigate('/abonnement'); break;
      case 'journal': navigate('/journal'); break;
      case 'weight': navigate('/poids'); break;
      case 'mealplan': navigate('/plans'); break;
      case 'scanner': navigate('/scanner'); break;
      case 'challenges': navigate('/defis'); break;
      case 'admin': navigate('/admin'); break;
      default: navigate('/');
    }
  };

  const handleAuthClick = (mode = 'signup') => {
    setAuthModalMode(mode);
    setShowAuthModal(true);
  };

  return (
    <div className="app">
      {/* Toast message d'authentification */}
      {authMessage && (
        <div className={`auth-toast ${authMessage.type}`} onClick={clearAuthMessage}>
          <span>{authMessage.text}</span>
          <button onClick={clearAuthMessage}>×</button>
        </div>
      )}

      <header className="mobile-header">
        <button className="menu-toggle" onClick={() => setSidebarOpen(!sidebarOpen)}>
          {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
        <div className="mobile-logo">
          <img src="./robot-icon.svg" alt="Louis" className="mobile-logo-icon" />
          <span>MonCoach</span>
        </div>
        {isAuthenticated && (
          <NotificationCenter userId={user?.id} />
        )}
      </header>

      <div className={`sidebar-wrapper ${sidebarOpen ? 'open' : ''}`}>
        <Sidebar 
          activePage={getActivePage()} 
          setActivePage={setActivePage}
          onAuthClick={handleAuthClick}
          onUpgradeClick={() => setShowUpgradeModal(true)}
        />
      </div>

      {sidebarOpen && (
        <div className="sidebar-overlay" onClick={() => setSidebarOpen(false)} />
      )}

      <main className="main-content">
        {children}
      </main>

      {showAuthModal && (
        <AuthModal 
          onClose={() => setShowAuthModal(false)} 
          initialMode={authModalMode}
        />
      )}

      {showUpgradeModal && (
        <UpgradeModal onClose={() => setShowUpgradeModal(false)} />
      )}
    </div>
  );
};

const AppContent = () => {
  const { isAuthenticated } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { trackPage } = useTracking();
  
  // États pour le parcours d'onboarding
  const [showQuiz, setShowQuiz] = useState(() => {
    const hasCompletedQuiz = localStorage.getItem('nb_quiz_completed');
    const hasSeenLanding = localStorage.getItem('nb_seen_landing');
    // Montrer le quiz seulement si pas encore fait et pas authentifié
    return !hasCompletedQuiz && !hasSeenLanding;
  });
  
  const [showLanding, setShowLanding] = useState(() => {
    const hasSeenLanding = localStorage.getItem('nb_seen_landing');
    const hasCompletedQuiz = localStorage.getItem('nb_quiz_completed');
    return !hasSeenLanding && hasCompletedQuiz;
  });

  // Si authentifié, ne plus montrer quiz ni landing
  useEffect(() => {
    if (isAuthenticated) {
      setShowQuiz(false);
      setShowLanding(false);
      localStorage.setItem('nb_seen_landing', 'true');
      localStorage.setItem('nb_quiz_completed', 'true');
    }
  }, [isAuthenticated]);

  // Scroll to top
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  // Tracking
  useEffect(() => {
    trackPage(location.pathname || 'home');
  }, [location.pathname, trackPage]);

  const handleQuizComplete = (answers) => {
    localStorage.setItem('nb_quiz_completed', 'true');
    localStorage.setItem('nb_quiz_answers', JSON.stringify(answers));
    localStorage.setItem('nb_seen_landing', 'true');
    setShowQuiz(false);
    setShowLanding(false);
    navigate('/');
  };

  const handleQuizSkip = () => {
    localStorage.setItem('nb_quiz_completed', 'true');
    localStorage.setItem('nb_seen_landing', 'true');
    setShowQuiz(false);
    setShowLanding(false);
    navigate('/');
  };

  const handleEnterApp = () => {
    setShowLanding(false);
    localStorage.setItem('nb_seen_landing', 'true');
    navigate('/');
  };

  // Pages légales - toujours accessibles sans layout
  const legalRoutes = ['/mentions-legales', '/confidentialite', '/cgv'];
  if (legalRoutes.includes(location.pathname)) {
    return (
      <Routes>
        <Route path="/mentions-legales" element={<MentionsLegalesPage />} />
        <Route path="/confidentialite" element={<PolitiqueConfidentialitePage />} />
        <Route path="/cgv" element={<CGVPage />} />
      </Routes>
    );
  }

  // Quiz d'onboarding pour les nouveaux visiteurs
  if (showQuiz && !isAuthenticated && location.pathname === '/') {
    return <OnboardingQuiz onComplete={handleQuizComplete} onSkip={handleQuizSkip} />;
  }

  // Landing page (si quiz skip ou après quiz mais pas inscrit)
  if (showLanding && !isAuthenticated && location.pathname === '/') {
    return <LandingPage onEnter={handleEnterApp} />;
  }

  // App principale avec layout
  return (
    <MainLayout>
      <Routes>
        <Route path="/" element={<ChatPage />} />
        <Route path="/recettes" element={<RecipesPage />} />
        <Route path="/profil" element={<ProfilePage />} />
        <Route path="/conseils" element={<TipsPage />} />
        <Route path="/abonnement" element={<SubscriptionPage />} />
        <Route path="/journal" element={<JournalPage />} />
        <Route path="/poids" element={<WeightPage />} />
        <Route path="/plans" element={<MealPlanPage />} />
        <Route path="/scanner" element={<ScannerPage />} />
        <Route path="/defis" element={<ChallengesPage />} />
        <Route path="/admin" element={<AdminPage />} />
      </Routes>
    </MainLayout>
  );
};

const App = () => {
  return (
    <HashRouter>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </HashRouter>
  );
};

export default App;
